import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  CreditCard, Building2, Receipt, Banknote, Check, 
  AlertTriangle, Sparkles, Clock, List, Layers
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useOracle } from "@/contexts/OracleContext";
import { TroquinhoCalendar } from "@/components/shared/TroquinhoCalendar";
import { 
  subscribeToTransactions, 
  toggleChecklistItem, 
  subscribeToChecklist 
} from "@/services/transactionsService";
import { subscribeToConnections } from "@/services/connectionsService";
import { parseISO, format } from "date-fns";

// Format currency
const formatCurrency = (value) => {
  return Math.abs(value).toLocaleString('pt-BR', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

// ==================== HELPERS DE PARCELAS ====================

// É parcela? Só no crédito, em série com mais de 1 mês.
// Se um dia existir o campo seriesKind = "recurring", ela deixa de ser tratada como parcela.
const isInstallment = (t) =>
  ["credit", "credito"].includes(t.paymentMethod) &&
  !!t.seriesId &&
  (t.seriesTotalMonths || 0) > 1 &&
  (t.seriesKind ?? "installment") === "installment";

// Data da compra original (parcela 1)
const getPurchaseDate = (t) => {
  const base = (t.date || t.dueDate || "").slice(0, 10);
  if (t.originalDate) return t.originalDate.slice(0, 10);
  if (!isInstallment(t) || !t.seriesIndex || t.seriesIndex <= 1 || !base) return base;
  const [y, m, d] = base.split("-").map(Number);
  const target = new Date(y, m - 1 - (t.seriesIndex - 1), 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  return format(target, "yyyy-MM-dd");
};

// Data usada para ordenar: parcelas 2/x em diante usam a data original (igual à fatura do banco)
const getSortDate = (t) => {
  if (isInstallment(t) && t.seriesIndex > 1) return getPurchaseDate(t);
  return (t.date || t.dueDate || "").slice(0, 10);
};

export default function PaymentChecklist() {
  const { user } = useAuth();
  const { isOracleActive } = useOracle();
  
  // 1. Estados de Período (Devem vir primeiro)
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // 2. Chave do mês e estados de dados
  const monthKey = `${currentYear}-${currentMonth}`;
  const [checkedItems, setCheckedItems] = useState({});
  const [subCheckedItems, setSubCheckedItems] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [connections, setConnections] = useState({ people: [], cards: [], banks: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showDrilldownSheet, setShowDrilldownSheet] = useState(false);
  const [drilldownMode, setDrilldownMode] = useState(() => {
    try { return localStorage.getItem("troquinho:drilldownMode") || "invoice"; }
    catch { return "invoice"; }
  });

  // 3. Efeito do Modo Oráculo (Sincroniza o calendário)
  useEffect(() => {
    const hoje = new Date();
    if (isOracleActive) {
      const dataFutura = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
      setCurrentMonth(dataFutura.getMonth());
      setCurrentYear(dataFutura.getFullYear());
    } else {
      setCurrentMonth(hoje.getMonth());
      setCurrentYear(hoje.getFullYear());
    }
  }, [isOracleActive]);

  // 4. Efeito para buscar os Checks no Firebase
 // Como deve ficar (Carregando ambos):
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToChecklist(user.uid, monthKey, (data) => {
      // Filtramos o que é check global e o que é sub-check (id de transação pura)
      const globals = {};
      const subs = {};
      
      Object.entries(data || {}).forEach(([key, value]) => {
        if (key.startsWith('bank-') || key.startsWith('card-') || key.startsWith('boleto-') || key === 'cash-group') {
          globals[key] = value;
        } else {
          subs[key] = value; // IDs de transações (sub-checks)
        }
      });

      setCheckedItems(globals);
      setSubCheckedItems(subs);
    });
    return () => unsubscribe();
  }, [user, monthKey]);

  // 5. Efeito para buscar transações e conexões
  useEffect(() => {
    if (!user) return;
    setIsLoading(true);
    const unsubscribeT = subscribeToTransactions(user.uid, currentMonth, currentYear, (data) => {
      setTransactions(data);
      setIsLoading(false);
    });
    const unsubscribeC = subscribeToConnections(user.uid, (data) => setConnections(data));
    return () => { unsubscribeT(); unsubscribeC(); };
  }, [user, currentMonth, currentYear]);

  // 6. Lógica de Agrupamento
  const periodTransactions = useMemo(() => {
    return transactions.filter(t => {
      if (t.type !== "expense") return false;
      const tDate = parseISO(t.dueDate || t.date);
      return tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear;
    });
  }, [transactions, currentMonth, currentYear]);

  const groupedData = useMemo(() => {
    const boletos = [];
    const bankGroups = new Map();
    const cardGroups = new Map();
    const cashGroup = [];

    periodTransactions.forEach(t => {
  // Mudamos para priorizar o valor total (amount) para conferência de fatura/extrato
  const amount = t.amount ?? 0; 
  if (t.paymentMethod === "boleto") { boletos.push(t); return; }
      if (["cash", "dinheiro"].includes(t.paymentMethod)) { cashGroup.push(t); return; }
      if (["pix", "debit", "debito"].includes(t.paymentMethod)) {
        const bankId = t.bankId || "unknown";
        if (!bankGroups.has(bankId)) {
          const bank = connections.banks.find(b => b.id === bankId);
          bankGroups.set(bankId, { id: bankId, name: bank?.name || "Banco", color: bank?.color || "#64748b", transactions: [], total: 0 });
        }
        const group = bankGroups.get(bankId);
        group.transactions.push(t);
        group.total += amount;
        return;
      }
      if (["credit", "credito"].includes(t.paymentMethod)) {
        const cardId = t.cardId || "unknown";
        const card = connections.cards.find(c => c.id === cardId);
        if (!cardGroups.has(cardId)) {
          cardGroups.set(cardId, { id: cardId, name: card?.name || "Cartão", color: card?.color || "#6366f1", dueDay: card?.dueDay || 10, transactions: [], total: 0 });
        }
        const group = cardGroups.get(cardId);
        group.transactions.push(t);
        group.total += amount;
        return;
      }
      boletos.push(t);
    });

         const sortT = (a, b) => getSortDate(b).localeCompare(getSortDate(a));
    bankGroups.forEach(g => g.transactions.sort(sortT));
    cardGroups.forEach(g => g.transactions.sort(sortT));
    boletos.sort(sortT);
    cashGroup.sort(sortT);

    return { boletos, banks: Array.from(bankGroups.values()), cards: Array.from(cardGroups.values()), cash: cashGroup };
  }, [periodTransactions, connections]);

  // 7. Montagem da lista do Checklist
  const checklistItems = useMemo(() => {
    const items = [];
    groupedData.boletos.forEach(t => items.push({
      id: `boleto-${t.id}`, type: "boleto", name: t.description, amount: t.amount,
      dueDate: t.dueDate, icon: Receipt, iconColor: "text-amber-600", bgColor: "bg-amber-100", 
      transactions: [t], isChecked: checkedItems[`boleto-${t.id}`] || false
    }));
    groupedData.banks.forEach(bank => items.push({
      id: `bank-${bank.id}`, type: "bank", name: bank.name, amount: bank.total, color: bank.color, 
      icon: Building2, iconColor: "text-blue-600", bgColor: "bg-blue-100", 
      transactions: bank.transactions, isChecked: checkedItems[`bank-${bank.id}`] || false
    }));
    groupedData.cards.forEach(card => items.push({
      id: `card-${card.id}`, type: "card", name: card.name, amount: card.total, color: card.color, 
      dueDay: card.dueDay, icon: CreditCard, iconColor: "text-indigo-600", bgColor: "bg-indigo-100", 
      transactions: card.transactions, isChecked: checkedItems[`card-${card.id}`] || false
    }));
    if (groupedData.cash.length > 0) {
      items.push({
        id: "cash-group", type: "cash", name: "Dinheiro", amount: groupedData.cash.reduce((sum, t) => sum + (t.amount), 0),
        icon: Banknote, iconColor: "text-emerald-600", bgColor: "bg-emerald-100", 
        transactions: groupedData.cash, isChecked: checkedItems["cash-group"] || false
      });
    }
    return items;
  }, [groupedData, checkedItems]);

  // 8. Função de Toggle (Firebase)
  const toggleChecked = async (itemId) => {
  // 1. Pegamos o estado atual
  const currentStatus = !!checkedItems[itemId];
  const newStatus = !currentStatus;

  // 2. Atualização Otimista: Muda na tela instantaneamente
  setCheckedItems(prev => ({
    ...prev,
    [itemId]: newStatus
  }));

  try {
    // 3. Tenta salvar no Firebase
    await toggleChecklistItem(user.uid, monthKey, itemId, newStatus);
    
    if (newStatus) {
      const item = checklistItems.find(i => i.id === itemId);
      toast.success(`${item?.name || 'Item'} conferido!`, {
        icon: "✅",
        duration: 2000
      });
    }
 } catch (error) {
    setCheckedItems(prev => ({ ...prev, [itemId]: currentStatus }));
    
    // Isso vai te mostrar no console (F12) o erro exato do Firebase
    console.error("ERRO REAL DO SERVIDOR:", error.code, error.message);
    
    toast.error(`Erro: ${error.message || "Falha na conexão"}`);
  }
};

  // 9. Lógica de Filtros e UI
  const filteredItems = useMemo(() => {
    if (activeFilter === "pending") return checklistItems.filter(item => !item.isChecked);
    if (activeFilter === "done") return checklistItems.filter(item => item.isChecked);
    return checklistItems;
  }, [checklistItems, activeFilter]);

  const progress = checklistItems.length ? Math.round((checklistItems.filter(i => i.isChecked).length / checklistItems.length) * 100) : 0;
  const totalPending = checklistItems.filter(i => !i.isChecked).reduce((sum, item) => sum + item.amount, 0);

  const getMotivationalPhrase = () => {
    if (progress === 100) return "Parabéns! Tudo conferido.";
    if (progress >= 50) return "Metade já foi!";
    return "Bora conferir esses extratos?";
  };

  const openDrilldown = (item) => {
    setSelectedGroup(item);
    setShowDrilldownSheet(true);
  };

    const changeDrilldownMode = (mode) => {
    if (mode === drilldownMode) return;
    setDrilldownMode(mode);
    try { localStorage.setItem("troquinho:drilldownMode", mode); } catch {}
    toast(mode === "invoice" ? "📄 Na ordem da fatura" : "🗂️ Separado: compras e parcelas", { duration: 1500 });
  };

  const groupHasInstallments = !!selectedGroup?.transactions.some(isInstallment);

  const drilldownSections = useMemo(() => {
    if (!selectedGroup) return [];
    const list = selectedGroup.transactions;
    if (drilldownMode !== "split" || !list.some(isInstallment)) {
      return [{ key: "all", title: null, items: list }];
    }
    return [
      { key: "month", title: "Compras do mês", items: list.filter(t => !isInstallment(t)) },
      { key: "installments", title: "Parcelas", items: list.filter(isInstallment) },
    ].filter(s => s.items.length > 0);
  }, [selectedGroup, drilldownMode]);

  return (
    <div className={`min-h-screen pb-32 transition-colors duration-500 ${isOracleActive ? 'bg-indigo-50/50' : 'bg-slate-50'}`}>
      <div className="max-w-lg mx-auto px-5 py-6">
        
        {/* Header */}
        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-5 text-center">
          <h1 className="text-2xl font-black tracking-tight text-slate-900 mb-1">Contas & Consequências</h1>
          <p className="text-slate-500 text-sm">Extrato e faturas do mês. Tudo pago?</p>
        </motion.header>

        {/* Progress Bar */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-5 rounded-[24px] mb-6 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-slate-600 tracking-tight">Progresso do mês</span>
            <span className={`text-sm font-black ${isOracleActive ? 'text-indigo-600' : 'text-emerald-600'}`}>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2.5 bg-slate-100" />
          <p className="text-[11px] text-slate-400 mt-2 font-medium">{getMotivationalPhrase()}</p>
        </motion.div>

        {/* Calendário */}
        <div className="flex justify-center mb-8">
          <TroquinhoCalendar 
            currentMonth={currentMonth} currentYear={currentYear} 
            onPeriodChange={(month, year) => { setCurrentMonth(month); setCurrentYear(year); }} 
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {["all", "pending", "done"].map(f => (
            <Button
              key={f} size="sm" variant={activeFilter === f ? "default" : "outline"}
              className={`rounded-full px-4 border-none shadow-sm ${activeFilter === f ? (isOracleActive ? "bg-indigo-600" : "bg-slate-800") : "bg-white text-slate-500"}`}
              onClick={() => setActiveFilter(f)}
            >
              {f === "all" ? "Tudo" : f === "pending" ? "Pendentes" : "Conferidos"}
            </Button>
          ))}
        </div>

        {/* Total Pending Card */}
        {totalPending > 0 && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className={`rounded-[24px] p-5 mb-6 text-white shadow-lg ${isOracleActive ? 'bg-indigo-600 shadow-indigo-200' : 'bg-rose-500 shadow-rose-200'}`}>
            <div className="flex items-center gap-2 mb-1 opacity-80">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-[10px] font-bold uppercase">Total Pendente</span>
            </div>
            <p className="text-3xl font-black">R$ {formatCurrency(totalPending)}</p>
          </motion.div>
        )}

        {/* List */}
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item, index) => (
              <ChecklistItem key={item.id} item={item} index={index} onToggle={() => toggleChecked(item.id)} onDrilldown={() => openDrilldown(item)} />
            ))}
          </AnimatePresence>
        </div>

        {/* Drill-down Sheet */}
        <Sheet open={showDrilldownSheet} onOpenChange={setShowDrilldownSheet}>
          <SheetContent side="bottom" className="rounded-t-[32px] px-6 pb-10 border-none">
                        <SheetHeader className="mb-4">
              <div className="flex items-center justify-between gap-3 pr-8">
                <SheetTitle className="flex items-center gap-3 min-w-0">
                  {selectedGroup && (
                    <div className={`w-10 h-10 rounded-xl ${selectedGroup.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <selectedGroup.icon className={`w-5 h-5 ${selectedGroup.iconColor}`} />
                    </div>
                  )}
                  <span className="font-black text-xl tracking-tight truncate">{selectedGroup?.name}</span>
                </SheetTitle>

                {groupHasInstallments && (
                  <div className="flex items-center bg-slate-100 rounded-full p-1 flex-shrink-0">
                    <button
                      type="button"
                      aria-label="Ordem da fatura"
                      onClick={() => changeDrilldownMode("invoice")}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        drilldownMode === "invoice" ? "bg-indigo-500 text-white shadow-md shadow-indigo-200" : "text-slate-400"
                      }`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Separar compras e parcelas"
                      onClick={() => changeDrilldownMode("split")}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        drilldownMode === "split" ? "bg-indigo-500 text-white shadow-md shadow-indigo-200" : "text-slate-400"
                      }`}
                    >
                      <Layers className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </SheetHeader>
               {/* Lista de Transações com Check Individual */}
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1 -mr-1 mt-4">
              {drilldownSections.map(section => (
                <div key={section.key} className="space-y-3">
                  {section.title && (
                    <div className="flex justify-between items-center px-1 pt-2">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        {section.title}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400">
                        R$ {formatCurrency(section.items.reduce((sum, t) => sum + (t.amount || 0), 0))}
                      </span>
                    </div>
                  )}

                  {section.items.map(t => {
                    const isSubChecked = subCheckedItems[t.id];
                    const installment = isInstallment(t);
                    const isLast = installment && t.seriesIndex === t.seriesTotalMonths;
                    const showPurchaseDate = installment && t.seriesIndex > 1;
                    const displayDate = showPurchaseDate ? getPurchaseDate(t) : (t.date || t.dueDate);

                    return (
                      <div
                        key={t.id}
                        onClick={() => {
                          const newStatus = !subCheckedItems[t.id];
                          toggleChecklistItem(user.uid, monthKey, t.id, newStatus);
                        }}
                        className={`flex items-center gap-3 p-3 rounded-2xl border transition-all ${
                          isSubChecked ? "bg-emerald-50/50 border-emerald-100 opacity-60" : "bg-slate-50 border-slate-100"
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 ${
                          isSubChecked ? "bg-emerald-500 border-emerald-500" : "border-slate-300 bg-white"
                        }`}>
                          {isSubChecked && <Check className="w-3 h-3 text-white" strokeWidth={4} />}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <span className="font-bold text-slate-700 text-sm truncate">{t.description}</span>
                            <span className="font-black text-rose-600 ml-2 whitespace-nowrap text-sm">
                              R$ {formatCurrency(t.amount)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-[10px] text-slate-400 font-bold">
                              {showPurchaseDate && "Compra em "}
                              {displayDate ? format(parseISO(displayDate), "dd/MM/yyyy") : "--"}
                            </span>
                            {installment && (
                              <Badge
                                variant="secondary"
                                className={`text-[8px] h-3.5 border-none font-black uppercase ${
                                  isLast ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                                }`}
                              >
                                {t.seriesIndex}/{t.seriesTotalMonths}{isLast && " · Última 🎉"}
                              </Badge>
                            )}
                            {t.isDivided && (
                              <Badge variant="secondary" className="text-[8px] h-3.5 bg-indigo-100 text-indigo-600 border-none font-black uppercase">
                                Dividido
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Rodapé do Modal: Totalizador de Conferência */}
            {selectedGroup && (
              <div className="mt-6 p-4 bg-slate-900 rounded-[20px] flex justify-between items-center text-white shadow-xl">
                <div>
                  <p className="text-[9px] font-bold opacity-50 uppercase tracking-widest">Total Conferido</p>
                  <p className="text-lg font-black leading-none">
                    R$ {formatCurrency(
                      selectedGroup.transactions
                        .filter(t => subCheckedItems[t.id])
                        .reduce((sum, t) => sum + t.amount, 0)
                    )}
                  </p>
                </div>
                <div className="bg-white/10 px-3 py-1 rounded-full text-right border border-white/10">
                  <p className="text-[9px] font-bold opacity-70 uppercase leading-none mb-1 text-center">Itens</p>
                  <p className="text-sm font-black text-center leading-none">
                    {selectedGroup.transactions.filter(t => subCheckedItems[t.id]).length} / {selectedGroup.transactions.length}
                  </p>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

      </div>
    </div>
  );
}

function ChecklistItem({ item, index, onToggle, onDrilldown }) {
  const Icon = item.icon;
  
  return (
    <motion.div 
      layout
      initial={{ opacity: 0, x: -10 }} 
      animate={{ opacity: 1, x: 0 }}
      // Mudamos o clique do card para não interferir se clicar no botão
      className={`bg-white rounded-[24px] p-4 shadow-sm border border-slate-100 flex items-center gap-4 transition-all ${
        item.isChecked ? "opacity-60 bg-slate-50/50" : "active:scale-[0.98]"
      }`}
      onClick={onDrilldown}
    >
      {/* Botão de Check - Ajustado */}
      <button 
        type="button" // Garante que não submeta formulários
        className={`relative z-10 w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
          item.isChecked 
            ? "bg-emerald-500 border-emerald-500 shadow-lg shadow-emerald-100" 
            : "border-slate-200 bg-white"
        }`}
        onClick={(e) => { 
          e.stopPropagation(); // Impede de abrir o modal (drilldown)
          onToggle(); 
        }}
      >
        {item.isChecked ? (
          <Check className="w-5 h-5 text-white" strokeWidth={3} />
        ) : (
          <div className="w-2 h-2 rounded-full bg-slate-100" />
        )}
      </button>

      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${item.bgColor}`}>
        <Icon className={`w-6 h-6 ${item.iconColor}`} />
      </div>

      <div className="flex-1 min-w-0">
        <p className={`font-bold text-slate-900 truncate transition-all ${
          item.isChecked ? "line-through text-slate-400" : ""
        }`}>
          {item.name}
        </p>
        {/* Bloco Ajustado Abaixo */}
        <div className="flex flex-col">
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            {item.transactions.length} transações
          </p>
          {item.transactions.length > 0 && !item.isChecked && (
            <p className="text-[10px] text-indigo-500 font-medium truncate mt-0.5">
              Último: {item.transactions[0].description} ({format(parseISO(item.transactions[0].date), "dd/MM")})
            </p>
          )}
        </div>
      </div>

      <div className="text-right">
        <p className={`font-black transition-colors ${
          item.isChecked ? "text-slate-300" : "text-rose-600"
        }`}>
          R$ {formatCurrency(item.amount)}
        </p>
      </div>
    </motion.div>
  );
}